
export const getTeamSizeGuidelines = (size: string, projectType?: string, duration?: number) => {
  const getProjectComplexity = (template?: string, projectDuration?: number) => {
    if (!template && !projectDuration) return 'medium';
    
    const templateLower = template?.toLowerCase() || '';
    const duration = projectDuration || 0;
    
    if (templateLower.includes('digital transformation') || 
        templateLower.includes('enterprise') || 
        templateLower.includes('migration') ||
        templateLower.includes('application managed services') ||
        templateLower.includes('infrastructure managed services') ||
        templateLower.includes('service provider network managed services') ||
        templateLower.includes('it network managed services') ||
        duration > 180) {
      return 'high';
    }
    
    if (templateLower.includes('marketing') || 
        templateLower.includes('campaign') || 
        templateLower.includes('event') ||
        duration < 60) {
      return 'low';
    }
    
    return 'medium';
  };

  const complexity = getProjectComplexity(projectType, duration);
  
  switch (size) {
    case "small":
      if (complexity === 'high') {
        return {
          description: "Small team for complex project (8-15 members): For high-complexity projects with limited scope or pilot phases.",
          guidelines: "Includes 1-2 senior directors, 2-3 experienced managers, 2-4 team leads, and 3-6 specialized team members. Focus on senior expertise and tight coordination."
        };
      } else if (complexity === 'low') {
        return {
          description: "Small team for simple project (3-8 members): Ideal for straightforward projects like marketing campaigns or small product updates.",
          guidelines: "Typically includes 1 project manager, 1-2 team leads, and 2-5 team members. Emphasize agility and quick execution."
        };
      }
      return {
        description: "Small team for moderate project (5-12 members): Suitable for focused projects with clear scope and moderate complexity.",
        guidelines: "Includes 1 project director/manager, 1-2 team leads, and 3-8 team members. Balance between expertise and efficiency."
      };
      
    case "medium":
      if (complexity === 'high') {
        return {
          description: "Medium team for complex project (20-35 members): For enterprise-scale projects requiring diverse expertise.",
          guidelines: "Includes 2-3 directors, 4-6 managers, 6-10 team leads, and 12-20 specialized team members. Strong governance and coordination required."
        };
      } else if (complexity === 'low') {
        return {
          description: "Medium team for simple project (8-18 members): For projects requiring multiple workstreams but straightforward execution.",
          guidelines: "Includes 1-2 managers, 2-4 team leads, and 6-12 team members. Focus on parallel execution and clear communication."
        };
      }
      return {
        description: "Medium team for moderate project (12-25 members): Standard for moderately complex projects with multiple phases.",
        guidelines: "Includes 1-2 directors, 3-5 managers, 4-7 team leads, and 8-15 team members. Balanced structure with clear hierarchy."
      };
      
    case "large":
      if (complexity === 'high') {
        return {
          description: "Large team for complex project (50-100 members): For major digital transformations, enterprise migrations, or multi-year initiatives.",
          guidelines: "Includes 3-5 directors, 8-12 managers, 12-18 team leads, and 25-65 specialized team members. Requires robust governance, PMO support, and sophisticated coordination."
        };
      } else if (complexity === 'low') {
        return {
          description: "Large team for simple project (25-50 members): For projects requiring scale but with straightforward requirements.",
          guidelines: "Includes 2-3 managers, 6-10 team leads, and 18-35 team members. Focus on efficient coordination and standardized processes."
        };
      }
      return {
        description: "Large team for moderate project (35-75 members): For complex, long-duration projects requiring diverse skills.",
        guidelines: "Includes 2-4 directors, 6-10 managers, 10-15 team leads, and 20-50 team members. Emphasis on structured governance and specialized expertise."
      };

    case "verylarge":
      if (complexity === 'high') {
        return {
          description: "Very Large team for complex project (150+ members): For massive enterprise transformations, global rollouts, or multi-location managed services.",
          guidelines: "Includes 5-10 directors, 15-25 managers, 25-40 team leads, and 100+ specialized team members across multiple locations. Requires enterprise-grade PMO, multiple governance layers, and sophisticated coordination mechanisms."
        };
      } else if (complexity === 'low') {
        return {
          description: "Very Large team for simple project (100-150 members): For large-scale rollouts with standardized processes.",
          guidelines: "Includes 3-5 managers, 15-25 team leads, and 80-120 team members. Focus on process standardization and efficient scaling."
        };
      }
      return {
        description: "Very Large team for moderate project (120-200 members): For enterprise-wide initiatives requiring extensive coordination.",
        guidelines: "Includes 3-6 directors, 12-20 managers, 20-35 team leads, and 85-140 team members. Requires sophisticated governance, clear escalation paths, and robust communication frameworks."
      };
      
    default:
      return {
        description: "Medium team configuration",
        guidelines: "Standard team composition"
      };
  }
};

export const formatDuration = (days?: number) => {
  if (!days && days !== 0) return "Not specified";
  if (days === 1) return "1 day";
  return `${days} days`;
};
